import { memo, useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Box,
  OptionPanelActionRow,
  OptionPanelNavRow,
  OptionPanelRow,
  OptionPanelSection,
  OptionPanelSeparator,
  PreviewArtThumbnail,
  PreviewRadioGroup,
  Switch,
  type PreviewRadioOption,
} from "@salestrak/ui";
import {
  MapLayerPreviewArt,
  MapLayerPreviewFrame,
  type MapLayerPreviewVariant,
} from "./map-layer-preview-art";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { CONTENT_ANCHOR_ID } from "#app/lib/hooks/use-anchor-insets";
import {
  DEFAULT_CARD_DISPLAY_OPTIONS,
  type CardDisplayOptions,
} from "#app/components/issue-card";
import {
  CardDisplayFields,
  CardLayoutRows,
  ModeRow,
  OptionsPanelShell,
  countShownCardFields,
} from "#app/components/view-options";
import type { MapSettings, MapShade } from "./map-styles";
import { AnalyticsContext, MapEvent, trackEvent } from "#app/lib/analytics";

export const DEFAULT_MAP_CARD_DISPLAY_OPTIONS: CardDisplayOptions = {
  ...DEFAULT_CARD_DISPLAY_OPTIONS,
  showAddress: false,
  showStage: true,
  showWorkflow: true,
};

/**
 * The panel's pages. The map is the view; its cards are what a pin opens.
 * So the landing page is the map's — its layers and the location dot — and
 * the cards live one door down, the way the board's Fields and Filters do.
 * The Fields roster is a page of its own under the cards page: three layers,
 * each backing out to the one above.
 */
type MapPanelPage = "main" | "cards" | "fields";

const PAGE_TITLES: Record<MapPanelPage, string> = {
  main: "Customize view",
  cards: "Project cards",
  fields: "Fields",
};

const BACK_LABELS: Record<Exclude<MapPanelPage, "main">, string> = {
  cards: "Back to Customize view",
  fields: "Back to Project cards",
};

/** The base map, pictured: a pair of cards with the name on the art. */
const BASE_MAP_OPTIONS: readonly PreviewRadioOption[] = [
  {
    value: "terrain",
    label: "Terrain",
    art: <MapLayerPreviewFrame variant="terrain" />,
  },
  {
    value: "hybrid",
    label: "Satellite",
    art: <MapLayerPreviewFrame variant="satellite" />,
  },
];

/** A detail layer's row picture — the same art as a card, framed small. */
function LayerThumbnail({ variant }: { variant: MapLayerPreviewVariant }) {
  return (
    <PreviewArtThumbnail>
      <MapLayerPreviewArt variant={variant} />
    </PreviewArtThumbnail>
  );
}

export type MapDisplayOptionsPanelProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cardOptions: CardDisplayOptions;
  onCardOptionsChange: (patch: Partial<CardDisplayOptions>) => void;
  fields: CustomFieldDef[];
  mapSettings: MapSettings;
  onMapSettingsChange: (settings: MapSettings) => void;
};

// Memoized: the map screen re-renders on each selection, and the closed
// panel does not depend on it.
export const MapDisplayOptionsPanel = memo(function MapDisplayOptionsPanel({
  open,
  onOpenChange,
  cardOptions,
  onCardOptionsChange,
  fields,
  mapSettings,
  onMapSettingsChange,
}: MapDisplayOptionsPanelProps) {
  const [page, setPage] = useState<MapPanelPage>("main");
  useEffect(() => {
    if (!open) setPage("main");
  }, [open]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) setPage("main");
      onOpenChange(next);
    },
    [onOpenChange],
  );
  const updateMapSettings = useCallback(
    (patch: Partial<MapSettings>) => {
      for (const [setting, value] of Object.entries(patch)) {
        trackEvent({
          context: AnalyticsContext.Map,
          event: MapEvent.ChangeDisplayOption,
          data: { setting, value },
        });
      }
      onMapSettingsChange({ ...mapSettings, ...patch });
    },
    [mapSettings, onMapSettingsChange],
  );
  const changeCardOptions = useCallback(
    (patch: Partial<CardDisplayOptions>, option?: string) => {
      trackEvent({
        context: AnalyticsContext.Map,
        event: MapEvent.ChangeCardOption,
        data: { option: option ?? Object.keys(patch).join(",") },
      });
      onCardOptionsChange(patch);
    },
    [onCardOptionsChange],
  );

  // The cards door and the Fields door both read the roster's count — the
  // pinned title aside.
  const shownCount = countShownCardFields(cardOptions, fields);
  const isSatellite = mapSettings.mapTypeId === "hybrid";

  let body: ReactNode;
  if (page === "fields") {
    body = (
      <CardDisplayFields
        fields={fields}
        onChange={changeCardOptions}
        options={cardOptions}
      />
    );
  } else if (page === "cards") {
    body = (
      <Box direction="column">
        {/* The deeper pages keep their titles (the board's More options
            grammar): a titled group names the rule its rows share. */}
        <OptionPanelSection title="Layout">
          <CardLayoutRows onChange={changeCardOptions} options={cardOptions} />
        </OptionPanelSection>

        <OptionPanelSeparator />

        <OptionPanelSection title="Content">
          <OptionPanelNavRow
            icon="square-pen"
            label="Fields"
            onClick={() => setPage("fields")}
            value={`${shownCount} shown`}
          />
        </OptionPanelSection>

        <OptionPanelSeparator />

        {/* The map's card options are a per-reader preference with no Save
            behind them, so the reset lands at once — the popup redraws with
            the map's own defaults (Stage and Workflow on, Address off: the
            popup's header already names the address). */}
        <OptionPanelSection>
          <OptionPanelActionRow
            icon="undo"
            label="Reset cards to defaults"
            onClick={() => changeCardOptions(DEFAULT_MAP_CARD_DISPLAY_OPTIONS, "reset")}
          />
        </OptionPanelSection>
      </Box>
    );
  } else {
    body = (
      <Box direction="column">
        {/* The landing page's groups carry no titles (the board's landing
            grammar) except Layers, whose rows are a roster with no lead-in
            of their own. The map's own rows lead. */}
        <OptionPanelSection title="Layers">
          {/* The base map as a pair of pictured cards, then each detail
              layer as a switch row led by a thumbnail of the same art.
              One list rhythm — the label is the row — and a new layer is one
              more row. The base cards sit on the rows' own 8px inset. */}
          <Box paddingX="sm" paddingBottom="sm">
            <PreviewRadioGroup
              aria-label="Base map"
              columns={2}
              labelPlacement="inside"
              onValueChange={(next) => {
                if (next === "terrain" || next === "hybrid") {
                  updateMapSettings({ mapTypeId: next });
                }
              }}
              options={BASE_MAP_OPTIONS}
              value={mapSettings.mapTypeId}
            />
          </Box>
          {/* A row that has nothing to control on the current base FOLDS
              AWAY rather than dimming: a dimmed row reads as broken, not
              resting (Spencer, 2026-09-09). `disabled` stays for rows that
              apply but the reader may not change. Traffic is an overlay and
              stays live over either base. */}
          {isSatellite ? (
            // Google's hybrid labels are one layer, so imagery gets ONE
            // switch where the styled map gets three.
            <OptionPanelRow
              label="Labels"
              visual={<LayerThumbnail variant="roads" />}
            >
              <Switch
                aria-label="Labels"
                checked={mapSettings.satelliteLabels}
                onCheckedChange={(satelliteLabels) =>
                  updateMapSettings({ satelliteLabels: !!satelliteLabels })
                }
                size="sm"
              />
            </OptionPanelRow>
          ) : (
            <>
              <OptionPanelRow
                label="Road names"
                visual={<LayerThumbnail variant="roads" />}
              >
                <Switch
                  aria-label="Road names"
                  checked={mapSettings.roadNames}
                  onCheckedChange={(roadNames) =>
                    updateMapSettings({ roadNames: !!roadNames })
                  }
                  size="sm"
                />
              </OptionPanelRow>
              <OptionPanelRow
                label="Places"
                visual={<LayerThumbnail variant="places" />}
              >
                <Switch
                  aria-label="Places"
                  checked={mapSettings.pointsOfInterest}
                  onCheckedChange={(pointsOfInterest) =>
                    updateMapSettings({
                      pointsOfInterest: !!pointsOfInterest,
                    })
                  }
                  size="sm"
                />
              </OptionPanelRow>
              <OptionPanelRow
                label="Transit"
                visual={<LayerThumbnail variant="transit" />}
              >
                <Switch
                  aria-label="Transit"
                  checked={mapSettings.transitLines}
                  onCheckedChange={(transitLines) =>
                    updateMapSettings({ transitLines: !!transitLines })
                  }
                  size="sm"
                />
              </OptionPanelRow>
            </>
          )}
          <OptionPanelRow
            label="Traffic"
            visual={<LayerThumbnail variant="traffic" />}
          >
            <Switch
              aria-label="Traffic"
              checked={mapSettings.traffic}
              onCheckedChange={(traffic) =>
                updateMapSettings({ traffic: !!traffic })
              }
              size="sm"
            />
          </OptionPanelRow>
        </OptionPanelSection>

        <OptionPanelSeparator />

        {/* Appearance modes, apart from the layer toggles: each is a choice
            among named values, so both wear the ModeRow grammar. */}
        <OptionPanelSection>
          {/* A SHADE of the current mode's own ground, never the other
              mode's palette: cross-theme tiles dragged the control fills
              with them and clashed with the themed popovers. Imagery has no
              shade, so the row folds away on satellite (see Layers). */}
          {isSatellite ? null : (
            <ModeRow<MapShade>
              label="Map brightness"
              value={mapSettings.mapShade}
              options={[
                { value: "lighter", label: "Lighter" },
                { value: "default", label: "Normal" },
                { value: "darker", label: "Darker" },
              ]}
              disabled={false}
              onChange={(mapShade) => updateMapSettings({ mapShade })}
            />
          )}
          {/* Melissa data: hidden = the pin card is street + city only.
              DOM-only, live over either base. */}
          <ModeRow<"show" | "hide">
            label="Property facts"
            value={mapSettings.propertyFacts ? "show" : "hide"}
            options={[
              { value: "show", label: "Show facts" },
              { value: "hide", label: "Hide facts" },
            ]}
            disabled={false}
            onChange={(next) =>
              updateMapSettings({ propertyFacts: next === "show" })
            }
          />
          {/* The imagery's counterpart to the shade row: a dark scrim over
              satellite, off by default (neither Apple nor Google dims
              imagery). It takes the shade row's turn — present only while
              imagery is the base. The why rides the row's `hint` glyph. */}
          {isSatellite ? (
            <OptionPanelRow
              label="Dim satellite imagery"
              hint="Darkens the imagery so pins and cards stand out"
            >
              <Switch
                aria-label="Dim satellite imagery"
                checked={mapSettings.dimSatellite}
                onCheckedChange={(dimSatellite) =>
                  updateMapSettings({ dimSatellite: !!dimSatellite })
                }
                size="sm"
              />
            </OptionPanelRow>
          ) : null}
        </OptionPanelSection>

        <OptionPanelSeparator />

        {/* Everything about the cards a pin opens sits behind one door, its
            value the fact a reader most often comes to change. */}
        <OptionPanelSection>
          <OptionPanelNavRow
            icon={ENTITY_KIND_ICON.issue}
            label="Project cards"
            onClick={() => setPage("cards")}
            value={`${shownCount} fields`}
          />
        </OptionPanelSection>

        <OptionPanelSeparator />

        {/* The reader's own dot on the map — a device setting, so it closes
            the page. Untitled like the groups above; the label names the
            noun itself. */}
        <OptionPanelSection>
          <OptionPanelRow
            label="High-accuracy location"
            hint="Uses device sensors. Increases battery usage."
          >
            <Switch
              aria-label="High-accuracy location"
              checked={mapSettings.accuracySensors}
              onCheckedChange={(accuracySensors) =>
                updateMapSettings({ accuracySensors: !!accuracySensors })
              }
              size="sm"
            />
          </OptionPanelRow>
        </OptionPanelSection>
      </Box>
    );
  }

  return (
    <OptionsPanelShell
      // The map has no below-tabs region, so the panel pins to the shell's
      // content card — inside the island, matching the map's filter panel.
      anchorId={CONTENT_ANCHOR_ID}
      backLabel={page === "main" ? undefined : BACK_LABELS[page]}
      fieldsPage={page === "fields"}
      onBack={
        page === "main"
          ? undefined
          : () => setPage(page === "fields" ? "cards" : "main")
      }
      onOpenChange={handleOpenChange}
      open={open}
      title={PAGE_TITLES[page]}
    >
      {body}
    </OptionsPanelShell>
  );
});
